import { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { X } from "lucide-react";
import { toast } from "sonner";

import { Checkbox } from "@/components/common/inputs";
import { Spinner } from "@/components/common/svgs";
import { fetchBatchOutputConfig, saveBatchOutputConfig } from "@/services/batchApi";
import { cn } from "@/utils/css";
import { DATE_RANGES, GEOGRAPHY_LEVELS, MAX_DATE_RANGES, RATE_MODES, RATE_VALUES } from "./batchConstants";

const ALL_RATE_VALUES = RATE_VALUES.map(({ value }) => value);

const toggle = (values, value) => (values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);

const SectionLabel = ({ children }) => (
    <span className="px-1 text-[12px] font-medium uppercase text-cm-p-text-secondary whitespace-nowrap">{children}</span>
);

const RateGroup = ({ label, rate, onChange }) => (
    <div className="flex flex-col gap-2 border border-cm-p-outline rounded-[8px] p-3">
        <Checkbox
            checked={rate.enabled}
            onChange={(enabled) => onChange({ enabled, selected: enabled ? ALL_RATE_VALUES : [] })}
            label={label}
            labelClassName="font-normal"
            className="h-6 p-1"
        />
        <div className={cn("flex items-center gap-5 h-6 p-1", { "opacity-40": !rate.enabled })}>
            {RATE_VALUES.map(({ value, label: valueLabel }) => (
                <Checkbox
                    key={value}
                    checked={rate.selected.includes(value)}
                    onChange={() => onChange({ ...rate, selected: toggle(rate.selected, value) })}
                    label={valueLabel}
                    labelClassName="font-normal"
                    disabled={!rate.enabled}
                    className="px-0 opacity-100"
                />
            ))}
        </div>
    </div>
);

const DateRangeChips = ({ selected, onChange }) => {
    const limitReached = selected.length >= MAX_DATE_RANGES;
    return (
        <div className="flex flex-wrap gap-3">
            {DATE_RANGES.map(({ value, label }) => {
                const isSelected = selected.includes(value);
                const disabled = limitReached && !isSelected;
                return (
                    <button
                        key={value}
                        type="button"
                        aria-pressed={isSelected}
                        disabled={disabled}
                        onClick={() => onChange(toggle(selected, value))}
                        className={cn("h-10 px-4 rounded-[58px] text-[14px] font-medium whitespace-nowrap", {
                            "ring-1 ring-inset ring-cm-p-accent-400 bg-cm-p-container-green text-cm-p-on-container-green": isSelected,
                            "bg-cm-p-surface-container text-cm-p-text-primary": !isSelected,
                            "hover:bg-cm-p-surface-container-hover": !isSelected && !disabled,
                            "opacity-40 cursor-not-allowed": disabled,
                        })}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
};

const SegmentedControl = ({ options, value, onChange }) => (
    <div role="tablist" className="flex items-center h-10 rounded-[100px] bg-cm-p-surface-container">
        {options.map((option) => {
            const isActive = option.value === value;
            return (
                <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onChange(option.value)}
                    className={cn("h-full rounded-[58px] px-4 text-[14px] text-cm-p-text-primary whitespace-nowrap", {
                        "border border-cm-p-outline bg-cm-p-surface font-medium": isActive,
                        "font-normal": !isActive,
                    })}
                >
                    {option.label}
                </button>
            );
        })}
    </div>
);

export const BatchOutputConfigModal = ({ isOpen, onClose }) => {
    const [config, setConfig] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return undefined;
        let ignore = false;
        setConfig(null);
        setLoadFailed(false);
        fetchBatchOutputConfig()
            .then((loaded) => {
                if (!ignore) setConfig(loaded);
            })
            .catch(() => {
                if (!ignore) setLoadFailed(true);
            });
        return () => {
            ignore = true;
        };
    }, [isOpen]);

    const update = (changes) => setConfig((current) => ({ ...current, ...changes }));

    const canSave = config !== null && !saving;

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            await saveBatchOutputConfig(config);
            toast.success("Output configuration saved", { className: "cm-toast-success", toasterId: "top-right" });
            onClose();
        } catch {
            toast.error("Could not save output configuration. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface border border-cm-p-outline rounded-[12px] w-[537px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] flex flex-col pointer-events-auto shadow-xl overflow-clip">
                <div className="flex items-center justify-between gap-[10px] border-b border-cm-p-outline pl-5 pr-1 py-4">
                    <div className="flex flex-col min-w-0">
                        <span className="text-[16px] font-medium text-cm-p-text-primary">Output configuration</span>
                        <span className="text-[12px] text-cm-p-text-secondary">
                            Select which details you would like to include in the batch rating output file
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex items-center justify-center h-10 px-4 rounded-[6px] hover:bg-cm-p-surface-hover text-cm-p-text-primary"
                    >
                        <X className="size-4" />
                    </button>
                </div>

                {config ? (
                    <div className="flex flex-col gap-7 px-5 pt-4 pb-6 overflow-y-auto">
                        <div className="flex flex-col gap-2">
                            <SectionLabel>I want to see</SectionLabel>
                            <RateGroup label="Market rate" rate={config.marketRate} onChange={(marketRate) => update({ marketRate })} />
                            <RateGroup
                                label="Custom company rate"
                                rate={config.companyRate}
                                onChange={(companyRate) => update({ companyRate })}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <SectionLabel>Date range in past (select up to {MAX_DATE_RANGES})</SectionLabel>
                            <DateRangeChips selected={config.pastRanges} onChange={(pastRanges) => update({ pastRanges })} />
                        </div>
                        <div className="flex flex-col gap-2">
                            <SectionLabel>Forecast date range (select up to {MAX_DATE_RANGES})</SectionLabel>
                            <DateRangeChips selected={config.forecastRanges} onChange={(forecastRanges) => update({ forecastRanges })} />
                        </div>
                        <div className="flex items-start justify-between gap-6">
                            <div className="flex flex-col items-start gap-2">
                                <SectionLabel>Geography level for lane data</SectionLabel>
                                <SegmentedControl
                                    options={GEOGRAPHY_LEVELS}
                                    value={config.geography}
                                    onChange={(geography) => update({ geography })}
                                />
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <SectionLabel>Rate mode</SectionLabel>
                                <SegmentedControl
                                    options={RATE_MODES}
                                    value={config.rateMode}
                                    onChange={(rateMode) => update({ rateMode })}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-[200px] px-5 text-[14px] text-cm-p-text-secondary">
                        {loadFailed ? (
                            "Could not load output configuration. Please try again."
                        ) : (
                            <Spinner className="w-5 h-5 text-cm-p-accent" />
                        )}
                    </div>
                )}

                <div className="flex items-center justify-end border-t border-cm-p-outline px-5 py-4">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className={cn("w-[120px] h-10 flex items-center justify-center rounded-[6px] text-[14px] font-medium", {
                            "bg-cm-p-accent-500 text-cm-p-neutral-dark-500": canSave,
                            "bg-cm-p-accent-50 text-cm-p-text-secondary cursor-not-allowed": !canSave,
                        })}
                    >
                        Save
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};
